import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { calculateContribution } from "@/components/calculator/utils/calculator";
import type { CalculationResult } from "@/components/calculator/utils/types";

export interface SavedCalculatorData {
  weeklyContribution: number;
  annualContribution: number;
  stateSupport: number | null;
  /** The nursing home cost used in the calculation (if any) */
  nursingHomeCost: number;
  /** Whether results came from a saved calculator run or are estimated */
  source: "calculator" | "none";
}

export function useSavedCalculatorResults() {
  const { user } = useAuth();

  return useQuery<SavedCalculatorData | null>({
    queryKey: ["saved-calculator-results", user?.id],
    queryFn: async () => {
      if (!user) return null;

      // Fetch the most recent calculator lead saved by this user's email
      const { data: authData } = await supabase.auth.getUser();
      const email = authData?.user?.email;
      if (!email) return null;

      const { data: leads } = await supabase
        .from("calculator_leads")
        .select("form_data, results")
        .eq("email", email)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (leads?.results) {
        const r = leads.results as any;
        return {
          weeklyContribution: r.weeklyContribution ?? 0,
          annualContribution: r.annualContribution ?? 0,
          stateSupport: r.stateSupport ?? null,
          nursingHomeCost: (leads.form_data as any)?.nursingHomeCost ?? 0,
          source: "calculator",
        };
      }

      return null;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}
